import { useState, useCallback, useMemo, FC, ReactNode } from 'react';

import { DevicePreviewNavigationContext } from './DevicePreviewNavigationContext';

interface DevicePreviewNavigationProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the Device Preview Navigation context.
 * Manages the Test Campaign modal open/close state and provides
 * stable callbacks via useMemo to prevent unnecessary re-renders.
 *
 * @example
 * <DevicePreviewNavigationProvider>
 *   <App />
 * </DevicePreviewNavigationProvider>
 */
export const DevicePreviewNavigationProvider: FC<DevicePreviewNavigationProviderProps> = ({
  children,
}) => {
  const [isTestCampaignModalOpen, setIsTestCampaignModalOpen] = useState(false);

  const openTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(true), []);
  const closeTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(false), []);

  const value = useMemo(
    () => ({ isTestCampaignModalOpen, openTestCampaignModal, closeTestCampaignModal }),
    [isTestCampaignModalOpen, openTestCampaignModal, closeTestCampaignModal],
  );

  return (
    <DevicePreviewNavigationContext.Provider value={value}>
      {children}
    </DevicePreviewNavigationContext.Provider>
  );
};
